import { useEffect } from 'react';

/**
 * iPad Chrome keyboard fix: Sets --vh CSS variable to window.innerHeight
 * instead of relying on 100vh, which includes hidden address bar/keyboard.
 * This prevents viewport shift and touch offset issues when keyboard opens.
 * 
 * Usage: const useViewportHeight = () => { ... }
 * Then in component: useViewportHeight();
 */
export function useViewportHeight() {
    useEffect(() => {
        let prevHeight = window.visualViewport
            ? window.visualViewport.height
            : window.innerHeight;

        const setViewportHeight = () => {
            const height = window.visualViewport
                ? window.visualViewport.height
                : window.innerHeight;
            const vh = height * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);

            // Keyboard closed: height increased back — scroll to top to remove dead space
            if (height > prevHeight) {
                window.scrollTo(0, 0);
            }
            prevHeight = height;
        };

        // Set on mount
        setViewportHeight();

        window.addEventListener('orientationchange', setViewportHeight);

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', setViewportHeight);
        } else {
            window.addEventListener('resize', setViewportHeight);
        }

        return () => {
            window.removeEventListener('orientationchange', setViewportHeight);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', setViewportHeight);
            } else {
                window.removeEventListener('resize', setViewportHeight);
            }
        };
    }, []);
}
